export type UITableValueType = 'string' | 'number' | 'currency' | 'percent' | 'date' | 'freeform';

export interface UITableColumnFormat {
    key: string;
    type: UITableValueType;
}

type UITableRow = Record<string, unknown>;

const normalizeRowUnit = (unit: unknown): UITableValueType | undefined => {
    if (typeof unit !== 'string') return undefined;

    switch (unit.trim().toLowerCase()) {
        case 'currency':
        case 'usd':
        case 'dollar':
        case 'dollars':
        case '$':
            return 'currency';
        case 'percent':
        case 'percentage':
        case '%':
            return 'percent';
        case 'number':
        case 'count':
        case 'shares':
        case 'units':
            return 'number';
        default:
            return undefined;
    }
};

/**
 * Returns the formatter for a cell. A row unit describes the primary measure
 * (for example, revenue is currency while margin is percent) and takes
 * precedence over a period column's default. Columns explicitly marked as
 * percent retain that formatting so a Change % column stays correct for every
 * metric in the table.
 */
export const getTableCellValueType = (
    row: UITableRow,
    column: UITableColumnFormat,
): UITableValueType => {
    if (column.type === 'string' || column.type === 'date' || column.type === 'percent' || column.type === 'freeform') {
        return column.type;
    }

    return normalizeRowUnit(row.unit ?? row.units) ?? column.type;
};

export const formatTableCellValue = (value: unknown, type: UITableValueType): string => {
    if (value === null || value === undefined) return '-';

    switch (type) {
        case 'currency': {
            const num = typeof value === 'number' ? value : parseFloat(String(value));
            if (isNaN(num)) return String(value);
            if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
            if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
            if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
            if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
            return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        case 'percent': {
            const pct = typeof value === 'number' ? value : parseFloat(String(value));
            if (isNaN(pct)) return String(value);
            return `${(pct * 100).toFixed(2)}%`;
        }
        case 'number': {
            const num = typeof value === 'number' ? value : parseFloat(String(value));
            if (isNaN(num)) return String(value);
            return num.toLocaleString('en-US');
        }
        case 'date':
            try {
                return new Date(String(value)).toLocaleDateString('en-US');
            } catch {
                return String(value);
            }
        default:
            return String(value);
    }
};
